import analytics from "../../analytics";
import { t } from "@/localization";
import { fetchSharedWithMe, fetchWorkspaceProfile, leaveTeam } from "./api";
import { teamsErrorMessage } from "./errors";
import { accessForOwner } from "./roles";
import {
  activeOwner,
  getWorkspaceState,
  persistWorkspaceOwner,
  setWorkspaceState,
} from "./workspace";

// Switching and checking the account the app works in (components/teams/workspace.js).

const SHARED_MAX_AGE_MS = 60 * 1000;
let sharedRequest = null;
let switchSeq = 0;

// sharedWithMe, at most once a minute unless forced. Keeps the last list when
// it fails (sharedError says why).
export async function refreshSharedAccess({ force = false } = {}) {
  const s = getWorkspaceState();
  if (!s.enabled || !s.self) return [];
  if (
    !force &&
    s.sharedLoaded &&
    !s.sharedError &&
    Date.now() - s.sharedAt < SHARED_MAX_AGE_MS
  ) {
    return s.shared;
  }
  if (sharedRequest && sharedRequest.self === s.self) {
    return sharedRequest.promise;
  }
  const self = s.self;
  const promise = (async () => {
    try {
      const shared = await fetchSharedWithMe();
      if (getWorkspaceState().self === self) {
        setWorkspaceState({
          shared,
          sharedLoaded: true,
          sharedAt: Date.now(),
          sharedError: null,
        });
      }
      return shared;
    } catch (err) {
      console.warn("sharedWithMe failed", err?.errors?.[0]?.message || err);
      if (getWorkspaceState().self === self) {
        setWorkspaceState({
          sharedLoaded: true,
          sharedAt: Date.now(),
          sharedError: teamsErrorMessage(err),
        });
      }
      return getWorkspaceState().shared;
    } finally {
      if (sharedRequest?.self === self) sharedRequest = null;
    }
  })();
  sharedRequest = { self, promise };
  return promise;
}

// Works in `owner`'s account from now on (null: your own). It first checks
// with the server: you must still be a member, and the server must answer in
// that account (for an owner Teams isn't on for, it answers with your own profile, and
// then nothing you did there would happen in their account). On any problem
// the app stays in, or goes back to, your own account.
// Resolves { ok, message?, access? }.
export async function switchWorkspace(
  owner,
  { source = "switcher", restoring = false } = {}
) {
  const start = getWorkspaceState();
  const self = start.self;
  if (!start.enabled || !self) {
    return { ok: false, message: t("Sign in first.") };
  }
  const seq = ++switchSeq;
  const stale = () => seq !== switchSeq || getWorkspaceState().self !== self;

  if (!owner || owner === self) {
    const was = activeOwner(start);
    setWorkspaceState({ owner: null, status: "own", profile: null });
    persistWorkspaceOwner(self, null);
    if (was && !restoring) {
      analytics.track("Workspace Switched", { to: "own", source });
    }
    return { ok: true };
  }

  setWorkspaceState({ owner, status: "checking", profile: null });
  const fail = (message) => {
    if (stale()) return { ok: false, stale: true };
    setWorkspaceState({
      owner: null,
      status: "own",
      profile: null,
      ...(restoring ? { notice: message } : {}),
    });
    persistWorkspaceOwner(self, null);
    return { ok: false, message };
  };

  await refreshSharedAccess({ force: !restoring });
  if (stale()) return { ok: false, stale: true };
  const { shared, sharedError } = getWorkspaceState();
  const access = accessForOwner(shared, owner);
  const name = access?.name || owner;
  if (!access) {
    return fail(
      sharedError ||
        t("You don't have access to {team}'s account any more.", {
          team: name,
        })
    );
  }

  let profile = null;
  try {
    profile = await fetchWorkspaceProfile(owner);
  } catch (err) {
    return fail(
      teamsErrorMessage(
        err,
        t("Couldn't open {team}'s account. Try again.", { team: name })
      )
    );
  }
  if (!profile || profile.username !== owner) {
    return fail(
      t("Teams isn't switched on for {team}'s account yet.", { team: name })
    );
  }
  if (stale()) return { ok: false, stale: true };

  setWorkspaceState({ status: "active", profile });
  persistWorkspaceOwner(self, owner);
  if (!restoring) {
    analytics.track("Workspace Switched", {
      to: "shared",
      role: access.role,
      scope: access.scope,
      source,
    });
  }
  return { ok: true, access };
}

// Once per sign-in: check the remembered account (or the ?as= link) with the
// server, and load what's shared with you.
export async function restoreWorkspace() {
  const s = getWorkspaceState();
  if (!s.enabled || !s.self || !s.restored || s.checkedFor === s.self) return;
  setWorkspaceState({ checkedFor: s.self });
  if (activeOwner(s)) {
    await switchWorkspace(s.owner, { restoring: true });
  } else {
    await refreshSharedAccess({ force: true });
  }
}

// The owner's profile again (after an unlock, a plan change…).
export async function refreshWorkspaceProfile() {
  const owner = activeOwner();
  if (!owner) return null;
  try {
    const profile = await fetchWorkspaceProfile(owner);
    if (activeOwner() === owner && profile?.username === owner) {
      setWorkspaceState({ profile });
    }
    return profile;
  } catch (err) {
    return null;
  }
}

// Leave an account someone shared (its whole-account membership and every
// event of it shared with you).
export async function leaveSharedAccount(entry) {
  if (!entry?.owner) return;
  if (entry.accountRole) await leaveTeam(entry.owner, null);
  for (const event of entry.events || []) {
    await leaveTeam(entry.owner, event.slug);
  }
  if (activeOwner() === entry.owner) {
    await switchWorkspace(null, { source: "leave" });
  }
  await refreshSharedAccess({ force: true });
}

// The one-off message (why the app went back to your account), once.
export function takeWorkspaceNotice() {
  const { notice } = getWorkspaceState();
  if (notice) setWorkspaceState({ notice: null });
  return notice;
}
